import {
  Fragment,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import { Stack, Tooltip } from '@mui/material'
import { ChevronRightRounded, HelpOutlineRounded } from '@mui/icons-material'
import { DiversityDimenstionConfig, Module, ModuleConfig } from '../../shared/model'
import { getFormattedString, getComparator } from '../../shared/utils'
import { FsOFAValue, Order } from '../../shared/model/models'
import FsOFATableHeader from './fs-ofa-table-header'
import FsOFASliderRow from './fs-ofa-slider-row'
import { ofaAPI } from '../../shared/api/OFAAPI'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { changeOFAOverall, setCWIMappings } from '../../store/actions'
import FsAlert, { FsAlertRef } from '../common/atoms/Alert'

export type OFATableRef = {
  saveActions: () => Promise<void>
  resetActions: () => Promise<void>
}

interface OFATableProps {
  moduleConfig: ModuleConfig | undefined
  diversityDimenstionConfig: DiversityDimenstionConfig | undefined
  setShouldRecalculate: (shouldRecalculate: boolean) => void
  rows: FsOFAValue[]
  contextModelVariantId: number
  goiLabel: string
}

const FsOFATable = forwardRef<OFATableRef, OFATableProps>(
  (
    {
      moduleConfig,
      diversityDimenstionConfig,
      setShouldRecalculate,
      rows,
      contextModelVariantId,
      goiLabel,
    },
    ref,
  ) => {
    const [order, setOrder] = useState<Order | null>('asc')
    const [orderBy, setOrderBy] =
      useState<keyof Omit<FsOFAValue, 'occupations'>>('ofaVariableLabel')
    const [selected, setSelected] = useState<number[]>([])
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const theme = useTheme()
    const { t, i18n } = useTranslation()
    const [lastUpdated, setLastUpdated] = useState<number[]>([])
    const [sliderChanges, setSliderChanges] = useState<
      Record<number, { curId: number; changedId: number | null }>
    >({})
    const dispatch = useAppDispatch()
    const alertRef = useRef<FsAlertRef>(null)
    const { currentRun, cwiMappings } = useAppSelector((state) => state.currentAnalysis)
    const { currentLanguage } = useAppSelector((state) => state.currentUser)

    useEffect(() => {
      if (currentRun && (!cwiMappings || !cwiMappings[currentRun.id])) {
        dispatch(setCWIMappings({ runId: currentRun.id }))
      }
    }, [currentRun])

    const handleRequestSort = (
      _: React.MouseEvent<unknown>,
      property: keyof Omit<FsOFAValue, 'occupations'>,
    ) => {
      const isAsc = orderBy === property && order === 'asc'
      if (order !== 'desc') {
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
      } else {
        setOrder(null)
      }
    }

    const handleClick = (_: any, id: number) => {
      const selectedIndex = selected.indexOf(id)
      let newSelected: number[] = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id)
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1))
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        )
      }
      setSelected(newSelected)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10))
      setPage(0)
    }

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0

    const currentRows = rows.filter((r) => r.isCurrentOption)
    const visibleRows = useMemo(
      () =>
        order
          ? [...currentRows]
              .sort(getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : [...currentRows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
      [order, orderBy, page, rowsPerPage, rows],
    )

    useImperativeHandle(ref, () => ({
      saveActions: async () => {
        try {
          const newOFAValues = [...rows]
          Object.entries(sliderChanges).forEach(([sch, schv]) => {
            if (schv.changedId) {
              const curID = newOFAValues.findIndex((fv) => fv.id === schv.curId)
              const changedID = newOFAValues.findIndex((fv) => fv.id === schv.changedId)
              newOFAValues[curID] = {
                ...newOFAValues[curID],
                isCurrentOption: false,
              }
              newOFAValues[changedID] = {
                ...newOFAValues[changedID],
                isCurrentOption: true,
              }
            }
          })
          const overallResults = await ofaAPI.getOFAOverall(newOFAValues)
          dispatch(changeOFAOverall(contextModelVariantId, overallResults))
          const newCurrentRows = newOFAValues.filter((r) => r.isCurrentOption)
          const newSliders: any = {}
          newCurrentRows.forEach((cr) => {
            newSliders[cr.ofaVariable] = { curId: cr.id, changedId: null }
          })
          setSliderChanges(newSliders)
        } catch (e) {
          alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
        } finally {
          setLastUpdated(selected)
          setSelected([])
        }
      },
      resetActions: async () => {
        try {
          const newOFAValues = [...rows]
          rows.forEach((origRow, index) => {
            if (origRow.isInitialOption) {
              newOFAValues[index] = {
                ...newOFAValues[index],
                isCurrentOption: true,
              }
            } else {
              newOFAValues[index] = {
                ...newOFAValues[index],
                isCurrentOption: false,
              }
            }
          })
          const overallResults = await ofaAPI.getOFAOverall(newOFAValues)
          dispatch(changeOFAOverall(contextModelVariantId, overallResults))
          const newCurrentRows = newOFAValues.filter((r) => r.isCurrentOption)
          const newSliders: any = {}
          newCurrentRows.forEach((cr) => {
            newSliders[cr.ofaVariable] = { curId: cr.id, changedId: null }
          })
          setSliderChanges(newSliders)
        } catch (e) {
          alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
        } finally {
          setLastUpdated([])
          setSelected([])
        }
      },
    }))

    useEffect(() => {
      const newSliders: any = {}
      currentRows.forEach((cr) => {
        newSliders[cr.ofaVariable] = { curId: cr.id, changedId: null }
      })
      setSliderChanges(newSliders)
    }, [])

    useEffect(() => {
      if (Object.values(sliderChanges).find((sc) => sc.changedId)) {
        setShouldRecalculate(true)
      } else {
        setShouldRecalculate(false)
      }
    }, [sliderChanges])

    return (
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer
            sx={{
              maxHeight:
                moduleConfig?.module === Module.TRAINING
                  ? 'calc(100vh - 436px)'
                  : 'calc(100vh - 386px)',
            }}
          >
            <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
              <FsOFATableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                moduleConfig={moduleConfig}
                diversityDimenstionConfig={diversityDimenstionConfig}
              />
              <TableBody>
                {visibleRows.map((row) => {
                  const isItemSelected = selected.includes(row.ofaVariable)
                  const isLastUpdated = lastUpdated.includes(row.ofaVariable)
                  return (
                    <Fragment key={`fragment_${row.id}`}>
                      <TableRow
                        onClick={(event) => {
                          if (row.newGap !== null) {
                            handleClick(event, row.ofaVariable)
                            setSliderChanges({
                              ...sliderChanges,
                              [row.ofaVariable]: {
                                ...sliderChanges[row.ofaVariable],
                                changedId: null,
                              },
                            })
                          }
                        }}
                        key={`row1_${row.id}`}
                        sx={{
                          cursor: row.newGap !== null ? 'pointer' : '',
                          backgroundColor: isLastUpdated
                            ? theme.palette.grey[theme.palette.mode === 'dark' ? '700' : '200']
                            : theme.palette.grey[theme.palette.mode === 'dark' ? '900' : '50'],
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                          '&.MuiTableRow-root td': {
                            borderTop: isItemSelected
                              ? `1px solid ${moduleConfig?.fsColor.primary}`
                              : `1px solid ${theme.palette.background.paper}`,
                            borderBottom: isItemSelected
                              ? `1px solid ${moduleConfig?.fsColor.primary}`
                              : `1px solid ${theme.palette.background.paper}`,
                          },
                          '&.MuiTableRow-root td:first-of-type': {
                            border: isItemSelected
                              ? `1px solid ${moduleConfig?.fsColor.primary}`
                              : '',
                            borderRight: 0,
                          },
                          '&.MuiTableRow-root td:last-child': {
                            border: isItemSelected
                              ? `1px solid ${moduleConfig?.fsColor.primary}`
                              : '',
                            borderLeft: 0,
                          },
                        }}
                      >
                        <TableCell>
                          {currentRun &&
                            cwiMappings &&
                            cwiMappings[currentRun.id] &&
                            cwiMappings[currentRun.id][row.ofaVariableLabel] && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography fontWeight="bold">{`${t(
                                      'etrl.kpi.occ.inc',
                                    )}:`}</Typography>
                                    {cwiMappings[currentRun.id][row.ofaVariableLabel].map((ob) => (
                                      <Box mt={1.5}>
                                        <Typography>{`\r\n\u2022 ${t(
                                          'pt.assistance.selected.occ.kldb',
                                        )} ${ob.kldb20105}`}</Typography>
                                        <Typography ml={1.5}>{`${
                                          currentLanguage === 'de'
                                            ? ob.bezeichnungKldb
                                            : ob.titleKldb
                                        }`}</Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                }
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      maxWidth: 1000,
                                    },
                                  },
                                }}
                                arrow
                                placement="right"
                              >
                                <Stack direction="row" alignItems="center" width="fit-content">
                                  {isLastUpdated && (
                                    <Typography mr={0.35} variant="h5">{`\u2022`}</Typography>
                                  )}
                                  <Typography>{parseInt(row.ofaVariableLabel, 10)}</Typography>
                                  <HelpOutlineRounded
                                    fontSize="small"
                                    sx={{ ml: 0.5, color: theme.palette.action.disabled }}
                                  />
                                </Stack>
                              </Tooltip>
                            )}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
                                {`Legend${currentLanguage === 'de' ? 'e' : ''}:\n`}
                                <Typography
                                  component="div"
                                  color="success.main"
                                  variant="h5"
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                >
                                  {`\u2022`}
                                  <Typography
                                    ml={1}
                                    mb={0.25}
                                    color="text.primary"
                                  >{`Gap <= 2 %`}</Typography>
                                </Typography>
                                <Typography
                                  component="div"
                                  color="warning.main"
                                  variant="h5"
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                >
                                  {`\u2022`}
                                  <Typography
                                    ml={1}
                                    mb={0.25}
                                    color="text.primary"
                                  >{`Gap > 2 %`}</Typography>
                                </Typography>
                                <Typography
                                  component="div"
                                  color="error.main"
                                  variant="h5"
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                >
                                  {`\u2022`}
                                  <Typography
                                    ml={1}
                                    mb={0.25}
                                    color="text.primary"
                                  >{`Gap > 5 %`}</Typography>
                                </Typography>
                              </Typography>
                            }
                            arrow
                            placement="right"
                          >
                            <Stack direction="row" alignItems="center" width="fit-content">
                              <Typography
                                color={`${
                                  Number(row.actualGap) > 5
                                    ? 'error'
                                    : Number(row.actualGap) > 2
                                    ? 'warning'
                                    : 'success'
                                }.main`}
                                mr={0.35}
                                mt={0.5}
                                variant="h5"
                              >{`\u2022`}</Typography>
                              <Typography>{`${getFormattedString(
                                i18n.language,
                                row.actualGap,
                                1,
                                1,
                              )} %`}</Typography>
                              <HelpOutlineRounded
                                fontSize="small"
                                sx={{ ml: 0.5, color: theme.palette.action.disabled }}
                              />
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
                                {t('ofa.empl.desc', {
                                  total: row.totalEmployeesConsidered,
                                  cwi: parseInt(row.ofaVariableLabel, 10),
                                  goi_number: row.totalEmployeesConcerned,
                                  goi_name: goiLabel,
                                })}
                              </Typography>
                            }
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 400,
                                },
                              },
                            }}
                            arrow
                            placement="right"
                          >
                            <Stack direction="row" alignItems="center" width="fit-content">
                              <Typography>{`${row.totalEmployeesConcerned} / ${row.totalEmployeesConsidered}`}</Typography>
                              <HelpOutlineRounded
                                fontSize="small"
                                sx={{ ml: 0.5, color: theme.palette.action.disabled }}
                              />
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography>{`${getFormattedString(
                            i18n.language,
                            row.currentCmAverage,
                            2,
                            2,
                          )} ${t(`${moduleConfig?.unit}` as any)}`}</Typography>
                        </TableCell>
                        {row.newGap === null ? (
                          <TableCell colSpan={4}>
                            <Typography>{t('no.action.desc', { goi_name: goiLabel })}</Typography>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell>
                              <Typography>{`${getFormattedString(
                                i18n.language,
                                row.newGap,
                                1,
                                1,
                              )} %`}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{`${getFormattedString(
                                i18n.language,
                                row?.additionalCmAverage ?? '',
                                2,
                                2,
                                true,
                              )} ${t(`${moduleConfig?.unit}` as any)}`}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography>{`${getFormattedString(
                                i18n.language,
                                row?.additionalCost ?? '',
                                2,
                                2,
                                true,
                              )} \u20ac`}</Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography>{`${getFormattedString(
                                  i18n.language,
                                  row?.costAsPercentageOfPayroll ?? '',
                                  0,
                                  3,
                                )} %`}</Typography>
                                <ChevronRightRounded
                                  sx={{
                                    transform: isItemSelected ? 'rotate(90deg)' : 'rotate(0deg)',
                                  }}
                                />
                              </Stack>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                      <FsOFASliderRow
                        rows={rows}
                        id={row.id}
                        ofaVariable={row.ofaVariable}
                        isItemSelected={isItemSelected}
                        moduleConfig={moduleConfig}
                        sliderChanges={sliderChanges}
                        onChange={(ofaVariable: number, changedId: number) => {
                          if (sliderChanges[ofaVariable].curId === changedId) {
                            setSliderChanges({
                              ...sliderChanges,
                              [row.ofaVariable]: {
                                ...sliderChanges[ofaVariable],
                                changedId: null,
                              },
                            })
                          } else {
                            setSliderChanges({
                              ...sliderChanges,
                              [ofaVariable]: {
                                ...sliderChanges[ofaVariable],
                                changedId,
                              },
                            })
                          }
                        }}
                      />
                      <TableRow
                        key={`row_divider_${row.id}`}
                        sx={{
                          height: 8,
                          display: 'table-row',
                        }}
                      >
                        <TableCell colSpan={12} />
                      </TableRow>
                    </Fragment>
                  )
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: 53 * emptyRows,
                    }}
                  >
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={currentRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            labelRowsPerPage={t('ofa.rows.per.page')}
          />
        </Paper>
        <FsAlert ref={alertRef} />
      </Box>
    )
  },
)

export default FsOFATable
